import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  TextField,
  MenuItem,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DatePicker from "react-multi-date-picker";
import { motion, AnimatePresence } from "framer-motion";
import { API_URL } from "../Config/api";

const PackageServiceDetails = forwardRef(
  (
    { accessToken, onPackageDataChange, prefillPackageId, prefillSchedules, packageDatesService },
    ref,
  ) => {
    console.log(packageDatesService, "packageDatesServicepackageDatesService")
    const [packages, setPackages] = useState([]);
    const [selectedPackageId, setSelectedPackageId] = useState("");
    const [packageDetails, setPackageDetails] = useState(null);

    // Master dates states
    const [masterDatesRaw, setMasterDatesRaw] = useState([]);
    const [masterDatesList, setMasterDatesList] = useState([]);
    const [openMasterModal, setOpenMasterModal] = useState(false);

    const [serviceSchedules, setServiceSchedules] = useState({});
    const [expandedPanel, setExpandedPanel] = useState(false);

    const [packageError, setPackageError] = useState(false);
    const [errors, setErrors] = useState({});

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
    });

    const handleSnackbarClose = (event, reason) => {
      if (reason === "clickaway") {
        return;
      }
      setSnackbar({ ...snackbar, open: false });
    };

    const getSrvKey = (srv, index) => {
      return srv.sub_service
        ? String(srv.sub_service)
        : `${srv.service_id}_${index}`;
    };

    const getUnscheduledPackageDates = (packageDates, schedules) => {
      const assignedDates = new Set();

      Object.values(schedules || {}).forEach((schedule) => {
        (schedule?.displayDates || []).forEach((date) =>
          assignedDates.add(date),
        );
      });

      return (packageDates || []).filter((date) => !assignedDates.has(date));
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        let newErrors = {};
        let firstErrorKey = null;
        let isPackageInvalid = !selectedPackageId;
        const missingPackageDates = getUnscheduledPackageDates(
          masterDatesList,
          serviceSchedules,
        );

        setPackageError(isPackageInvalid);

        if (packageDetails && packageDetails.Included_service) {
          packageDetails.Included_service.forEach((srv, index) => {
            const srvKey = getSrvKey(srv, index);
            const sched = serviceSchedules[srvKey];

            const hasValidDateCount =
              sched?.displayDates?.length === srv.duration;
            const hasTime = !!sched?.start_time;

            if (!hasValidDateCount || !hasTime) {
              newErrors[srvKey] = {
                date: !hasValidDateCount,
                time: !hasTime,
              };
              if (!firstErrorKey) firstErrorKey = srvKey;
            }
          });
        }

        setErrors(newErrors);

        if (isPackageInvalid) return false;
        if (firstErrorKey) {
          setExpandedPanel(firstErrorKey);
        }
        if (missingPackageDates.length > 0) {
          return `Assign every package date to at least one service schedule. Dates not scheduled: ${missingPackageDates.join(", ")}`;
        }
        if (firstErrorKey) {
          return false;
        }

        return missingPackageDates.length === 0;
      },
    }));

    const handleAccordionChange = (panel) => (event, isExpanded) => {
      setExpandedPanel((prev) => (prev === panel ? false : panel));
    };

    const formatDateStr = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    useEffect(() => {
      if (
        Array.isArray(packageDatesService) &&
        packageDatesService.length > 0
      ) {
        const formattedDates = packageDatesService
          .map((item) => {
            const dateValue = Array.isArray(item) ? item[0] : item;

            if (!dateValue) return null;

            const date = dateValue?.toDate
              ? dateValue.toDate()
              : new Date(dateValue);

            if (Number.isNaN(date.getTime())) return null;

            return formatDateStr(date);
          })
          .filter(Boolean);

        const uniqueDates = [...new Set(formattedDates)].sort(
          (a, b) => new Date(a) - new Date(b)
        );

        setMasterDatesList(uniqueDates);

        setMasterDatesRaw(
          uniqueDates.map((date) => new Date(date))
        );
      }
    }, [packageDatesService]);

    useEffect(() => {
      const fetchPackages = async () => {
        try {
          const res = await fetch(`${API_URL}/packages/active_packages/`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          });
          const json = await res.json();
          if (json.data) {
            setPackages(json.data);
          }
        } catch (error) {
          console.error("Error fetching packages:", error);
        }
      };
      fetchPackages();
    }, [accessToken]);

    // === NAYA LOGIC: Handle Master Date Change ===
    const handleMasterDateChange = (dates) => {
      let expanded = [];
      const rawArray = Array.isArray(dates) ? dates : dates ? [dates] : [];

      rawArray.forEach((item) => {
        if (Array.isArray(item) && item.length === 2) {
          let startDt = item[0]?.toDate ? item[0].toDate() : new Date(item[0]);
          let endDt = item[1]?.toDate ? item[1].toDate() : new Date(item[1]);
          startDt.setHours(0, 0, 0, 0);
          endDt.setHours(0, 0, 0, 0);
          if (startDt > endDt) {
            let t = startDt;
            startDt = endDt;
            endDt = t;
          }
          for (
            let d = new Date(startDt);
            d <= endDt;
            d.setDate(d.getDate() + 1)
          ) {
            expanded.push(formatDateStr(d));
          }
        } else {
          let d = Array.isArray(item) ? item[0] : item;
          if (d) {
            let dt = d?.toDate ? d.toDate() : new Date(d);
            expanded.push(formatDateStr(dt));
          }
        }
      });

      // Dates ko sort karke unique banaya gaya hai
      const uniqueDates = [...new Set(expanded)].sort(
        (a, b) => new Date(a) - new Date(b),
      );

      setMasterDatesList(uniqueDates);
      setMasterDatesRaw(dates);

      // Parent ko package_dates pass kiya gaya (4th argument)
      onPackageDataChange(
        selectedPackageId,
        serviceSchedules,
        packageDetails?.package_cost,
        uniqueDates,
      );
    };

    const handlePackageSelect = async (e) => {
      const pkgId = e.target.value;

      setSelectedPackageId(pkgId);
      setPackageError(false);
      setErrors({});
      setExpandedPanel(false);
      setServiceSchedules({});
      setPackageDetails(null);
      setMasterDatesRaw([]);
      setMasterDatesList([]);

      try {
        const res = await fetch(
          `${API_URL}/packages/package_details/?package_id=${pkgId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );
        const json = await res.json();
        if (json.data && json.data.length > 0) {
          const details = json.data[0];
          setPackageDetails(details);

          const initialSchedules = {};
          details.Included_service.forEach((srv, index) => {
            const srvKey = getSrvKey(srv, index);
            initialSchedules[srvKey] = {
              start_time: "",
              end_time: "",
              dateRaw: [],
              date: [],
              displayDates: [],
            };
          });

          console.log(initialSchedules, "initialSchedulesinitialSchedules")

          setServiceSchedules(initialSchedules);
          // 4th argument empty array pass kiya gaya start mein
          onPackageDataChange(
            pkgId,
            initialSchedules,
            details.package_cost,
            [],
          );
          setOpenMasterModal(true);
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      }
    };

    const calculateEndTime = (start, hours) => {
      if (!start || !hours) return "";
      const [h, m] = start.split(":").map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return "";
      const totalMinutes = h * 60 + m + hours * 60;
      const endH = Math.floor(totalMinutes / 60) % 24;
      const endM = Math.round(totalMinutes % 60);
      return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
    };

    const handleStartTimeChange = (srvKey, value, serviceHours) => {
      const endTime = calculateEndTime(value, serviceHours);
      const updatedSchedules = {
        ...serviceSchedules,
        [srvKey]: {
          ...serviceSchedules[srvKey],
          start_time: value,
          end_time: endTime,
        },
      };
      setServiceSchedules(updatedSchedules);
      // masterDatesList pass kiya as package_dates
      onPackageDataChange(
        selectedPackageId,
        updatedSchedules,
        packageDetails?.package_cost,
        masterDatesList,
      );
    };

    const buildPrefillSchedules = (details, rawSchedules) => {
      const schedules = {};
      let allPrefillDates = [];

      details.Included_service.forEach((srv, index) => {
        const srvKey = getSrvKey(srv, index);
        const raw = rawSchedules?.[srvKey] || rawSchedules?.[srv.service_id];

        // if (raw?.start_date && raw?.end_date) {
        //   const start = new Date(raw.start_date);
        //   const end = new Date(raw.end_date);
        //   const displayDates = [];
        //   for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        //     displayDates.push(formatDateStr(d));
        //   }
        //   allPrefillDates.push(...displayDates);

        //   schedules[srvKey] = {
        //     start_time: raw.start_time || "",
        //     end_time: raw.end_time || "",
        //     dateRaw: displayDates,
        //     date: displayDates.map((d) => [d, d]),
        //     displayDates,
        //   };
        // } 
        if (raw?.start_date && raw?.end_date) {
          const start = new Date(raw.start_date);
          const end = new Date(raw.end_date);

          console.log(start, end, "enq dates")

          const displayDates = [];
          const dateObjects = [];


          for (
            let d = new Date(start);
            d <= end;
            d.setDate(d.getDate() + 1)
          ) {
            displayDates.push(formatDateStr(d));
            console.log(displayDates, "displayDates enq")

            // DatePicker needs Date objects
            dateObjects.push(new Date(d));
          }

          allPrefillDates.push(...displayDates);

          schedules[srvKey] = {
            start_time: raw.start_time || "",
            end_time: raw.end_time || "",
            dateRaw: dateObjects,
            date: displayDates.map((d) => [d, d]),
            displayDates,
          };
        }
        else {
          schedules[srvKey] = {
            start_time: "",
            end_time: "",
            dateRaw: [],
            date: [],
            displayDates: [],
          };
        }
      });

      return { schedules, allPrefillDates };
    };

    useEffect(() => {
      const loadPrefillPackage = async () => {
        if (!prefillPackageId) return;
        setSelectedPackageId(prefillPackageId);
        setPackageError(false);
        try {
          const res = await fetch(
            `${API_URL}/packages/package_details/?package_id=${prefillPackageId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          );
          const json = await res.json();
          if (json.data && json.data.length > 0) {
            const details = json.data[0];
            setPackageDetails(details);
            const { schedules, allPrefillDates } = buildPrefillSchedules(
              details,
              prefillSchedules,
            );
            console.log(allPrefillDates, "allPrefillDatesallPrefillDates")
            const uniquePrefills = [...new Set(allPrefillDates)].sort(
              (a, b) => new Date(a) - new Date(b),
            );

            if (
              !Array.isArray(packageDatesService) ||
              packageDatesService.length === 0
            ) {
              setMasterDatesList(uniquePrefills);
              setMasterDatesRaw(uniquePrefills);
            }

            setServiceSchedules(schedules);
            // setMasterDatesList(uniquePrefills);
            // setMasterDatesRaw(uniquePrefills);

            // setServiceSchedules(schedules);

            // prefill ke waqt bhi package_dates bheja gaya
            onPackageDataChange(
              prefillPackageId,
              schedules,
              details.package_cost,
              uniquePrefills,
            );

            if (details.Included_service.length > 0) {
              setExpandedPanel(getSrvKey(details.Included_service[0], 0));
            }
          }
        } catch (error) {
          console.error("Error prefilling package details:", error);
        }
      };
      loadPrefillPackage();
}, [prefillPackageId, accessToken, packageDatesService]);

    const handleDateChange = (srvKey, selectedDatesArray, maxDuration) => {
      const rawArray = Array.isArray(selectedDatesArray)
        ? selectedDatesArray
        : selectedDatesArray
          ? [selectedDatesArray]
          : [];

      let expandedDates = [];

      for (let i = 0; i < rawArray.length; i++) {
        let item = rawArray[i];

        if (Array.isArray(item) && item.length === 2 && item[0] && item[1]) {
          let startDt = item[0]?.toDate ? item[0].toDate() : new Date(item[0]);
          let endDt = item[1]?.toDate ? item[1].toDate() : new Date(item[1]);
          startDt.setHours(0, 0, 0, 0);
          endDt.setHours(0, 0, 0, 0);

          if (startDt > endDt) {
            let t = startDt;
            startDt = endDt;
            endDt = t;
          }

          for (
            let d = new Date(startDt);
            d <= endDt;
            d.setDate(d.getDate() + 1)
          ) {
            let dStr = formatDateStr(d);
            if (masterDatesList.includes(dStr)) {
              if (!expandedDates.includes(dStr)) {
                expandedDates.push(dStr);
              }
            }
          }
        } else {
          let d = Array.isArray(item) ? item[0] : item;
          if (d) {
            let dt = d?.toDate ? d.toDate() : new Date(d);
            let dStr = formatDateStr(dt);
            if (masterDatesList.includes(dStr)) {
              if (!expandedDates.includes(dStr)) {
                expandedDates.push(dStr);
              }
            }
          }
        }
      }

      if (expandedDates.length > maxDuration) {
        setSnackbar({
          open: true,
          message: `Oops! You can only select up to ${maxDuration} date(s) for this service.`,
        });
        return false;
      }

      const backendFormatted = expandedDates.map((dStr) => [dStr, dStr]);

      const updatedSchedules = {
        ...serviceSchedules,
        [srvKey]: {
          ...serviceSchedules[srvKey],
          dateRaw: selectedDatesArray,
          date: backendFormatted,
          displayDates: expandedDates,
        },
      };

      setServiceSchedules(updatedSchedules);

      // 4th argument me masterDatesList pass kiya gaya
      onPackageDataChange(
        selectedPackageId,
        updatedSchedules,
        packageDetails?.package_cost,
        masterDatesList,
      );
    };

    const isServiceValid = (srvKey, duration) => {
      const sched = serviceSchedules[srvKey];
      return sched?.displayDates?.length === duration && !!sched?.start_time;
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const containerVariants = {
      hidden: { opacity: 0 },
      show: {
        opacity: 1,
        transition: {
          staggerChildren: 0.1,
        },
      },
    };

    const itemVariants = {
      hidden: { opacity: 0, y: 20 },
      show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 300, damping: 24 },
      },
    };

    const cardVariants = {
      hidden: { opacity: 0, scale: 0.95, y: 15 },
      show: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { type: "spring", stiffness: 250, damping: 20 },
      },
      exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.2 } },
    };

    return (
      <Box sx={{ width: "100%", mt: 1, boxSizing: "border-box" }}>
        {/* ==== PREMIUM PACKAGE DROPDOWN ==== */}
        <Box sx={{ width: "100%" }}>
          <TextField
            select
            fullWidth
            label="Select  Package"
            value={selectedPackageId}
            onChange={handlePackageSelect}
            error={packageError}
            helperText={
              packageError ? "Please select a package to continue" : ""
            }
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                    overflowY: "auto",
                    mt: 1,
                    borderRadius: "16px",
                    boxShadow: "0 12px 48px rgba(0,0,0,0.12)",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: "8px",
                    },
                  },
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                transition: "all 0.2s ease-in-out",
                "&:hover fieldset": {
                  borderColor: "#3b82f6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2563eb",
                  borderWidth: "2px",
                },
              },
            }}
          >
            {packages.map((pkg) => (
              <MenuItem key={pkg.package_id} value={pkg.package_id}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  alignItems="center"
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      color="#1e293b"
                    >
                      {pkg.package_name}
                    </Typography>
                  </Box>
                  <Chip
                    label={`${pkg.duration} Days Plan`}
                    size="small"
                    sx={{
                      ml: 2,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      backgroundColor: "#eff6ff",
                      color: "#2563eb",
                      border: "1px solid #bfdbfe",
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* ================= COMPACT SUMMARY CARD FOR MASTER DATES ================= */}
        <AnimatePresence>
          {packageDetails && (
            <Box
              component={motion.div}
              variants={cardVariants}
              initial="hidden"
              animate="show"
              exit="exit"
              sx={{ width: "100%", mt: 2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
                }}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1.5}
                  sx={{ width: "100%", boxSizing: "border-box" }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                      component={motion.div}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "inset 0px 2px 4px rgba(255,255,255,0.6)",
                      }}
                    >
                      <AssignmentTurnedInIcon
                        sx={{ color: "#0284c7", fontSize: 20 }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="#0f172a"
                        lineHeight={1.2}
                        mb={0.25}
                      >
                        Package Dates
                      </Typography>
                      <Typography
                        variant="caption"
                        color={
                          masterDatesList.length > 0 ? "#059669" : "#64748b"
                        }
                        fontWeight={masterDatesList.length > 0 ? 600 : 500}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {masterDatesList.length > 0 ? (
                          <>
                            <Box
                              component="span"
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "#10b981",
                                boxShadow: "0 0 0 2px rgba(16, 185, 129, 0.2)",
                              }}
                            />
                            {masterDatesList.length} dates selected
                          </>
                        ) : (
                          "No dates selected"
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {masterDatesList.length > 0 ? (
                      <Tooltip title="Edit Dates" arrow>
                        <IconButton
                          onClick={() => setOpenMasterModal(true)}
                          size="small"
                          sx={{
                            border: "1.5px solid #2563eb",
                            borderRadius: "8px",
                            color: "#2563eb",
                            backgroundColor: "transparent",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => setOpenMasterModal(true)}
                        sx={{
                          borderRadius: "6px",
                          p: "4px 12px",
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor: "#2563eb",
                          color: "#fff",
                          textTransform: "none",
                          minWidth: "auto",
                        }}
                      >
                        Select Dates
                      </Button>
                    )}
                  </motion.div>
                </Box>

                {masterDatesList.length > 0 && (
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    mt={1.5}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {masterDatesList.slice(0, 2).map((d) => (
                      <Chip
                        key={d}
                        label={d}
                        size="small"
                        icon={
                          <CalendarMonthIcon
                            sx={{
                              fontSize: "14px !important",
                              color: "#64748b",
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: "#f8fafc",
                          border: "1px solid #cbd5e1",
                          color: "#334155",
                          fontWeight: 500,
                          px: 0.5,
                          height: "26px",
                          flexShrink: 0,
                        }}
                      />
                    ))}

                    {masterDatesList.length > 2 && (
                      <Tooltip
                        title={masterDatesList.join(", ")}
                        placement="top"
                        arrow
                      >
                        <Chip
                          label={`+${masterDatesList.length - 2} more`}
                          size="small"
                          sx={{
                            backgroundColor: "#f1f5f9",
                            border: "1px dashed #94a3b8",
                            color: "#475569",
                            fontWeight: 600,
                            height: "26px",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </AnimatePresence>

        {/* ================= MASTER DATE PICKER MODAL ================= */}
        <Dialog
          open={openMasterModal}
          onClose={() => setOpenMasterModal(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px", overflow: "hidden", m: 2 },
          }}
        >
          <Box
            sx={{
              bgcolor: "#63b386",
              color: "#fff",
              py: 2,
              px: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: "1.15rem" }}
            >
              Configure Package Dates
            </Typography>

            <IconButton
              onClick={() => setOpenMasterModal(false)}
              size="small"
              sx={{
                color: "#fff",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select up to {packageDetails?.duration} days for this package.
            </Typography>

            <Box sx={{ width: "100%", mb: 1 }}>
              <DatePicker
                multiple
                range
                value={masterDatesRaw}
                minDate={today}
                onChange={handleMasterDateChange}
                containerStyle={{ width: "100%" }}
                position="bottom-left"
                portal
                fixMainPosition
                offsetY={8}
                zIndex={9999}
                render={(value, openCalendar) => (
                  <TextField
                    onClick={openCalendar}
                    label="Select Active Service Dates *"
                    placeholder="YYYY/MM/DD"
                    fullWidth
                    size="medium"
                    value={
                      masterDatesList.length > 0
                        ? masterDatesList.join(", ")
                        : ""
                    }
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        cursor: "pointer",
                        transition: "0.2s",
                        "&:hover": { borderColor: "#63b386" },
                      },
                    }}
                  />
                )}
              />
            </Box>

            <AnimatePresence>
              <Box
                component={motion.div}
                layout
                mt={2}
                p={2}
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  bgcolor:
                    masterDatesList.length > packageDetails?.duration
                      ? "#fef2f2"
                      : "#f0fdf4",
                  borderRadius: "8px",
                  textAlign: "center",
                  border: "1px solid",
                  borderColor:
                    masterDatesList.length > packageDetails?.duration
                      ? "#fecaca"
                      : "#bbf7d0",
                  transition: "background-color 0.3s ease",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color={
                    masterDatesList.length > packageDetails?.duration
                      ? "#ef4444"
                      : "#059669"
                  }
                >
                  Selected: {masterDatesList.length} of{" "}
                  {packageDetails?.duration} Days
                </Typography>
                {masterDatesList.length > packageDetails?.duration && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Typography
                      variant="caption"
                      color="#dc2626"
                      sx={{ display: "block", mt: 0.5, fontWeight: 500 }}
                    >
                      Limit exceeded! Please remove extra dates.
                    </Typography>
                  </motion.div>
                )}
              </Box>
            </AnimatePresence>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0, justifyContent: "space-between" }}>
            <Button
              // onClick={() => {
              //   setMasterDatesRaw([]);
              //   setMasterDatesList([]);
              //   // Jab dates clear hon, parent ko bhi empty array bhejo
              //   onPackageDataChange(
              //     selectedPackageId,
              //     serviceSchedules,
              //     packageDetails?.package_cost,
              //     [],
              //   );
              // }}

              onClick={() => {
                setMasterDatesRaw([]);
                setMasterDatesList([]);

                const clearedSchedules = { ...serviceSchedules };

                Object.keys(clearedSchedules).forEach((key) => {
                  clearedSchedules[key] = {
                    ...clearedSchedules[key],
                    dateRaw: [],
                    date: [],
                    displayDates: [],
                  };
                });

                setServiceSchedules(clearedSchedules);

                onPackageDataChange(
                  selectedPackageId,
                  clearedSchedules,
                  packageDetails?.package_cost,
                  [],
                );
              }}
              sx={{ color: "#64748b", fontWeight: 600, textTransform: "none" }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={() => setOpenMasterModal(false)}
              sx={{
                px: 4,
                borderRadius: "8px",
                fontWeight: 600,
                textTransform: "none",
                bgcolor: "#63b386",
                "&:hover": { bgcolor: "#4a9c6d" },
              }}
              disabled={
                masterDatesList.length === 0 ||
                masterDatesList.length > packageDetails?.duration
              }
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================= SERVICE SCHEDULE SECTION ================= */}
        {packageDetails && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            sx={{
              mt: 1,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Typography
              variant="subtitle1"
              mb={2}
              fontWeight={700}
              color="#0f172a"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <MedicalServicesIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
              Service Schedules
            </Typography>

            <Box
              component={motion.div}
              variants={containerVariants}
              initial="hidden"
              animate="show"
              sx={{
                maxHeight: 450,
                overflowY: "auto",
                pr: 1,
                pb: 2,
                "&::-webkit-scrollbar": { width: "5px" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "10px",
                },
              }}
            >
              {packageDetails.Included_service.map((srv, index) => {
                const srvKey = getSrvKey(srv, index);
                const isValid = isServiceValid(srvKey, srv.duration);
                const hasError = errors[srvKey];
                const isExpanded = expandedPanel === srvKey;
                const selectedCount =
                  serviceSchedules[srvKey]?.displayDates?.length || 0;

                return (
                  <motion.div variants={itemVariants} key={srvKey}>
                    <Accordion
                      expanded={isExpanded}
                      onChange={handleAccordionChange(srvKey)}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid",
                        borderColor: isExpanded
                          ? "#93c5fd"
                          : hasError
                            ? "#fca5a5"
                            : "#e2e8f0",
                        background: isExpanded
                          ? "#f8fafc"
                          : hasError
                            ? "#fef2f2"
                            : "#fff",
                        boxShadow: isExpanded
                          ? "0 4px 12px rgba(0,0,0,0.04)"
                          : "0 2px 6px rgba(0,0,0,0.02)",
                        "&:before": { display: "none" },
                        transition: "all 0.3s ease",
                        overflow: "hidden",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            color={isExpanded ? "primary" : "action"}
                          />
                        }
                        sx={{ minHeight: "68px" }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          width="100%"
                        >
                          {hasError ? (
                            <ErrorOutlineIcon sx={{ color: "#ef4444" }} />
                          ) : isValid ? (
                            <CheckCircleOutlineIcon sx={{ color: "#10b981" }} />
                          ) : (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#94a3b8",
                                mx: 0.5,
                              }}
                            />
                          )}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: hasError
                                  ? "#b91c1c"
                                  : isExpanded
                                    ? "#1d4ed8"
                                    : "#334155",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                              }}
                            >
                              {srv.service_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="#64748b"
                              fontWeight={500}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
                              {srv.sub_service_name} • Assign {srv.duration}{" "}
                              Shift(s)
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          pt: 2,
                          pb: 3,
                          px: { xs: 2, md: 3 },
                          borderTop: "1px solid",
                          borderColor: isExpanded ? "#e0f2fe" : "#f1f5f9",
                          bgcolor: "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2.5,
                            width: "100%",
                          }}
                        >
                          {/* Row 1: Date Picker */}
                          <Box sx={{ width: "100%" }}>
                            <DatePicker
                              multiple
                              range
                              format="DD/MM/YYYY"
                              value={serviceSchedules[srvKey]?.dateRaw || []}
                              portal
                              fixMainPosition
                              position="bottom-left"
                              offsetY={8}
                              zIndex={9999}
                              minDate={today}
                              mapDays={({ date }) => {
                                let dateStr = date.format("YYYY-MM-DD");
                                let isSelectable =
                                  masterDatesList.includes(dateStr);
                                if (!isSelectable) {
                                  return {
                                    disabled: true,
                                    style: {
                                      color: "#cbd5e1",
                                      textDecoration: "line-through",
                                      backgroundColor: "#f8fafc",
                                    },
                                  };
                                }
                              }}
                              onChange={(dates) =>
                                handleDateChange(srvKey, dates, srv.duration)
                              }
                              containerStyle={{ width: "100%" }}
                              render={(pickerValue, openCalendar) => (
                                <TextField
                                  onClick={() => {
                                    if (masterDatesList.length > 0) {
                                      openCalendar();
                                    }
                                  }}
                                  label={`Select ${srv.duration} Date(s) for this service`}
                                  size="medium"
                                  fullWidth
                                  value={pickerValue || ""}
                                  InputProps={{
                                    readOnly: true,
                                    sx: {
                                      borderRadius: "12px",
                                      bgcolor: "#f8fafc",
                                      cursor:
                                        masterDatesList.length === 0
                                          ? "not-allowed"
                                          : "pointer",
                                      transition: "0.3s ease",
                                      "&:hover": { borderColor: "#3b82f6" },
                                    },
                                  }}
                                  error={
                                    masterDatesList.length === 0 ||
                                    !!errors[srvKey]?.date
                                  }
                                  helperText={
                                    masterDatesList.length === 0
                                      ? "Please configure Overall Package Dates first!"
                                      : errors[srvKey]?.date
                                        ? `Action required: Select exactly ${srv.duration} date(s)`
                                        : `Assigned: ${selectedCount} of ${srv.duration} Days`
                                  }
                                />
                              )}
                            />
                          </Box>

                          {/* Row 2: Time Inputs (Side by side) */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 2,
                              width: "100%",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                type="time"
                                label="Shift Start"
                                size="medium"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={
                                  serviceSchedules[srvKey]?.start_time || ""
                                }
                                error={!!errors[srvKey]?.time}
                                helperText={
                                  errors[srvKey]?.time
                                    ? "Start time is required"
                                    : ""
                                }
                                InputProps={{
                                  sx: {
                                    borderRadius: "12px",
                                    bgcolor: "#f8fafc",
                                    transition: "all 0.3s",
                                  },
                                }}
                                onChange={(e) =>
                                  handleStartTimeChange(
                                    srvKey,
                                    e.target.value,
                                    srv.Service_Time,
                                  )
                                }
                              />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <TextField
                                type="time"
                                label="Shift End (Auto)"
                                size="medium"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                  readOnly: true,
                                  sx: {
                                    borderRadius: "12px",
                                    bgcolor: "#f1f5f9",
                                    color: "#64748b",
                                  },
                                }}
                                value={serviceSchedules[srvKey]?.end_time || ""}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </motion.div>
                );
              })}
            </Box>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="error"
            variant="filled"
            sx={{ width: "100%", borderRadius: "12px", boxShadow: 3 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  },
);

export default PackageServiceDetails;
